<template>
  <div class="blog-headline">
    <v-card dark elevation="0" class="rounded-lg">
      <v-img src="/images/cover.jpeg" gradient="to bottom, rgba(63,81,181,.3), rgba(63,81,181,.95)" :height="heroHeight"
        class="blog-hero">
        <v-container class="fill-height" :class="{ 'px-4': $vuetify.breakpoint.smAndDown }">
          <v-row align="center" justify="center">
            <v-col cols="12" md="10" lg="8">
              <!-- Hero Text -->
              <div class="text-center hero-content">
                <h1 class="font-weight-bold hero-title" :class="titleClass">
                  {{ $t('header.blog') }}
                </h1>
                <p class="font-weight-light hero-subtitle" :class="subtitleClass">
                  {{ $t('landing.blogDesc') }}
                </p>

                <!-- Search Bar -->
                <v-card class="search-card mx-auto" :max-width="searchMaxWidth" elevation="8">
                  <v-card-text :class="searchPadding">
                    <v-text-field v-model="searchQuery"
                      :placeholder="$t('blog.searchPlaceholder') || 'Search articles...'"
                      prepend-inner-icon="mdi-magnify" dense solo flat hide-details light clearable
                      @input="onSearchInput" @keyup.enter="onSearch" class="search-input">
                      <template v-slot:append>
                        <v-btn color="primary" depressed class="search-btn" :small="$vuetify.breakpoint.xs"
                          @click="onSearch">
                          <v-icon v-if="$vuetify.breakpoint.xs" small>mdi-magnify</v-icon>
                          <span v-else>{{ $t('blog.search') || 'Search' }}</span>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-img>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchQuery: ''
    }
  },
  computed: {
    heroHeight() {
      // Responsive hero heights
      if (this.$vuetify.breakpoint.xs) return '400px'
      if (this.$vuetify.breakpoint.sm) return '450px'
      if (this.$vuetify.breakpoint.md) return '500px'
      return '550px'
    },
    titleClass() {
      // Responsive title classes
      if (this.$vuetify.breakpoint.xs) return 'text-h4 mb-3'
      if (this.$vuetify.breakpoint.sm) return 'text-h3 mb-4'
      if (this.$vuetify.breakpoint.md) return 'display-1 mb-4'
      return 'display-2 mb-5'
    },
    subtitleClass() {
      // Responsive subtitle classes
      if (this.$vuetify.breakpoint.xs) return 'text-body-1 mb-6'
      if (this.$vuetify.breakpoint.sm) return 'text-h6 mb-6'
      if (this.$vuetify.breakpoint.md) return 'headline mb-8'
      return 'headline mb-8'
    },
    searchMaxWidth() {
      // Responsive search bar width
      if (this.$vuetify.breakpoint.xs) return '100%'
      if (this.$vuetify.breakpoint.sm) return '550px'
      return '700px'
    },
    searchPadding() {
      // Responsive search padding
      return this.$vuetify.breakpoint.xs ? 'pa-1' : 'pa-2'
    }
  },
  methods: {
    onSearchInput(value) {
      // Emit search query changes
      this.$emit('search', value || '')
    },
    onSearch() {
      this.$emit('search', this.searchQuery)
    }
  }
}
</script>

<style scoped lang="scss">
@import "./blog-style";

.blog-headline {
  margin: -24px -24px 24px -24px;

  .blog-hero {
    position: relative;
    overflow: hidden;
  }

  .hero-content {
    animation: fadeInUp 0.8s ease-out;
    padding: 0 8px;
  }

  .hero-title {
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
    letter-spacing: 1px;
    line-height: 1.2;
  }

  .hero-subtitle {
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3);
    opacity: 0.95;
    line-height: 1.4;
    max-width: 90%;
    margin-left: auto;
    margin-right: auto;
  }

  .search-card {
    border-radius: 50px !important;
    overflow: hidden;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.98) !important;

    ::v-deep .v-input__slot {
      box-shadow: none !important;
    }

    .search-input {
      ::v-deep .v-input__prepend-inner {
        margin-top: 8px !important;
      }

      ::v-deep .v-text-field__slot input {
        font-size: 14px;
      }
    }

    .search-btn {
      border-radius: 50px;
      text-transform: none;
      font-weight: 600;
      padding: 0 24px !important;
      min-width: auto !important;
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Extra Small devices (phones, 600px and down)
@media (max-width: 599px) {
  .blog-headline {
    margin: -16px -16px 16px -16px;

    .hero-content {
      padding: 0 4px;
    }

    .hero-title {
      text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.4);
      letter-spacing: 0.5px;
    }

    .hero-subtitle {
      font-size: 0.95rem !important;
      max-width: 95%;
      line-height: 1.3;
    }

    .search-card {
      border-radius: 30px !important;

      .search-input {
        ::v-deep .v-text-field__slot input {
          font-size: 13px;
        }

        ::v-deep .v-input__prepend-inner {
          margin-top: 6px !important;

          .v-icon {
            font-size: 20px;
          }
        }
      }

      .search-btn {
        padding: 0 12px !important;

        ::v-deep .v-icon {
          font-size: 18px;
        }
      }
    }
  }
}

// Small devices (tablets, 600px to 959px)
@media (min-width: 600px) and (max-width: 959px) {
  .blog-headline {
    margin: -20px -20px 20px -20px;

    .hero-title {
      letter-spacing: 0.8px;
    }

    .hero-subtitle {
      max-width: 85%;
    }

    .search-card {
      .search-input {
        ::v-deep .v-text-field__slot input {
          font-size: 14px;
        }
      }
    }
  }
}

// Medium devices (small laptops, 960px to 1263px)
@media (min-width: 960px) and (max-width: 1263px) {
  .blog-headline {
    .hero-subtitle {
      max-width: 80%;
    }
  }
}

// Large devices (desktops, 1264px and up)
@media (min-width: 1264px) {
  .blog-headline {
    .hero-title {
      letter-spacing: 1.5px;
    }

    .hero-subtitle {
      max-width: 75%;
      font-size: 1.5rem !important;
    }
  }
}

// Landscape orientation adjustments for mobile
@media (max-width: 959px) and (orientation: landscape) {
  .blog-headline {
    .blog-hero {
      ::v-deep .v-image__image {
        background-position: center center;
      }
    }

    .hero-content {
      padding: 0 16px;
    }

    .hero-title {
      margin-bottom: 12px !important;
    }

    .hero-subtitle {
      margin-bottom: 16px !important;
      font-size: 0.9rem !important;
    }
  }
}
</style>